import { useTranslation } from 'react-i18next';
import i18n from 'src/i18n';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SyncIcon from '@mui/icons-material/Sync';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import IOSSwitch from 'src/components/switch/switch.component';
import { CommonHelper } from 'src/utils/commonHelper';
import { useToaster } from 'src/utils/toaster/toasterContext';

export default function WhatsAppAccounts({
  accounts = [],
  isLoading,
  onConnect,
  onReauthenticate,
  onRefresh,
  refreshingAccount,
  onDelete,
  onToggleEnable,
}) {
  useTranslation();
  const { showToast } = useToaster();
  const canIntegrate = CommonHelper.HasProfessionalPlan();

  const copyWhatsAppURL = (url) => {
    if (!url) return;
    navigator.clipboard
      .writeText(url)
      .then(() => showToast('WhatsApp chat link copied', 's'))
      .catch(() => showToast('Unable to copy WhatsApp chat link', 'e'));
  };

  if (!isLoading && !accounts.length) {
    return (
      <Grid container>
        <Grid item xs={12}>
          <Box sx={{ py: 5, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
            <Typography fontWeight={800}>No integrated WhatsApp accounts</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
              {canIntegrate ? 'Add a WhatsApp number to receive messages in WhatsApp Chat.' : i18n.t('plan_access.whatsapp_upgrade')}
            </Typography>
            <Button variant="contained" onClick={onConnect} disabled={!canIntegrate}>
              Add WhatsApp Number
            </Button>
          </Box>
        </Grid>
      </Grid>
    );
  }

  if (!accounts.length) return null;

  return (
    <TableContainer component={Card} variant="outlined" sx={{ borderRadius: 1 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Phone number</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="right">WhatsApp Chat</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {accounts.map((account) => {
            const phoneNumber = account.phone_number || '';
            const whatsAppWebURL = account.profile_link || '';
            const isActive = account.is_active !== false;
            const isEnabled = account.is_enable !== false;

            return (
              <TableRow key={account.id} hover>
                <TableCell>
                  <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
                    <Avatar
                      src={account.picture_url || ''}
                      sx={{ width: 34, height: 34, borderRadius: 1, bgcolor: alpha('#25D366', 0.12), color: '#128C7E' }}
                    >
                      <WhatsAppIcon fontSize="small" />
                    </Avatar>
                    <Typography fontWeight={800} noWrap>
                      {account.display_name || 'WhatsApp Business'}
                    </Typography>
                  </Stack>
                </TableCell>
                <TableCell>{phoneNumber || account.profile_id || '-'}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap alignItems="center">
                    <Chip
                      size="small"
                      color={isActive ? 'success' : 'error'}
                      label={isActive ? 'Active' : 'Inactive'}
                    />
                    <Chip
                      size="small"
                      color={isEnabled ? 'primary' : 'default'}
                      label={isEnabled ? 'Enabled' : 'Disabled'}
                    />
                  </Stack>
                </TableCell>
                <TableCell align="right">
                  {whatsAppWebURL ? (
                    <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="flex-end">
                      <Typography
                        component="a"
                        href={whatsAppWebURL}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          color: 'primary.main',
                          fontWeight: 700,
                          textDecoration: 'none',
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        {whatsAppWebURL}
                      </Typography>
                      <Tooltip title="Copy link">
                        <IconButton size="small" onClick={() => copyWhatsAppURL(whatsAppWebURL)}>
                          <ContentCopyIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  ) : (
                    '-'
                  )}
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                    <Tooltip
                      title={
                        isActive ? 'Enable or disable this account' : 'Reconnect this WhatsApp number to activate it'
                      }
                    >
                      <span>
                        <IOSSwitch
                          checked={isEnabled && isActive}
                          disabled={!isActive}
                          onChange={(event) => onToggleEnable('whatsapp', account, event.target.checked)}
                        />
                      </span>
                    </Tooltip>
                    <Button size="small" variant="outlined" disabled={!canIntegrate} onClick={() => onReauthenticate(account)}>
                      Re-authenticate
                    </Button>
                    <Tooltip title="Refresh business name and picture">
                      <span>
                        <IconButton
                          size="small"
                          aria-label="Refresh WhatsApp business details"
                          disabled={refreshingAccount === `whatsapp:${account.id}`}
                          onClick={() => onRefresh('whatsapp', account)}
                        >
                          <SyncIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      startIcon={<DeleteOutlineIcon />}
                      onClick={() => onDelete('whatsapp', account)}
                    >
                      Delete
                    </Button>
                  </Stack>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
